import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import BrandHeader from "../components/BrandHeader";
import CheckinForm from "../components/weekly/CheckinForm";
import AccountabilityMessage from "../components/weekly/AccountabilityMessage";
import WeeklyInsights from "../components/weekly/WeeklyInsights";
import { InsightsHistory, ResultsSnapshot } from "../components/weekly/HistoryViews";
import { OpenState, ClosedState, CompletedState, LockedState } from "../components/weekly/WindowStates";
import { useClientAuth } from "../lib/clientAuthContext";
import { clientLogin } from "../lib/clientAuth";
import { getWeeklyStatus, submitWeeklySetup } from "../lib/weekly";

// Client-facing Weekly Check-In hub (Stage 2B + 2C).
// Flow:  unauthenticated  ->  inline sign-in
//        authenticated + no roadmap  ->  roadmap-gate screen
//        authenticated + roadmap done + no setup  ->  2-question setup
//        fully set up  ->  window state (open / closed / completed) -> 8-question form

const SIGNIN_PHOTO = "https://customer-assets-lqy194kg.emergentagent.net/job_habit-profile/artifacts/1cy87ebo_LM_180718_JustineJeddy_0234-Edit_MEDres%20%28logo%29.jpg";

const LOCK_AFTER_RATING_MS = 8000; // time the final completion message stays visible before the tab locks

const INTRO_COPY =
  "This space is *yours*, come here each week to receive personalised insights, guidance and practical problem solving where you need to most to accelerate your results and get the most out of this journey 💛";

function Intro({ showBodyCopy = true }) {
  return (
    <div className="max-w-2xl">
      <div className="label-eyebrow flex items-center gap-3" data-testid="weekly-intro-eyebrow">
        <span className="inline-block h-px w-10 bg-brand-primary" />
        Weekly Check In &amp; Accountability Hub
      </div>
      <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-brand-ink leading-tight mt-4" data-testid="weekly-heading">
        Welcome to your <span className="font-bold text-[#C8965A]" data-testid="weekly-heading-brand">HER Aligned</span> Weekly Check In &amp; Accountability Hub
      </h1>
      <p className="mt-6 font-semibold text-brand-ink text-base md:text-lg" data-testid="weekly-window-note">
        Your Weekly Check In is open every week from Friday 4pm to Sunday 11pm
      </p>
      {showBodyCopy && (
        <p className="mt-5 text-brand-ink-muted text-base md:text-lg leading-relaxed" data-testid="weekly-body-copy">
          {INTRO_COPY.split("*yours*").map((part, i) =>
            i === 0 ? part : <span key={i}><em className="text-brand-ink not-italic font-semibold">yours</em>{part}</span>
          )}
        </p>
      )}
    </div>
  );
}

function SignInPanel({ onSignedIn }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await clientLogin({ email: form.email.trim(), password: form.password });
      await onSignedIn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign you in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="surface-card mt-10 overflow-hidden" data-testid="weekly-signin-panel">
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        {/* Photo — original 3:2 proportions, never cropped or stretched */}
        <div className="relative bg-[#EFE6DA] p-4 md:p-5 flex flex-col justify-center" data-testid="weekly-signin-photo-wrap">
          <div className="w-full rounded-2xl overflow-hidden ring-1 ring-brand-border shadow-sm">
            <img
              src={SIGNIN_PHOTO}
              alt="Coach Justine coaching a HER Aligned client through a kettlebell movement"
              className="block w-full h-auto aspect-[3/2] object-contain"
              loading="lazy"
              decoding="async"
              data-testid="weekly-signin-photo"
            />
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-brand-ink-muted text-center" data-testid="weekly-signin-photo-caption">
            Coached, supported and held accountable — every single week
          </p>
        </div>

        {/* Form */}
        <div className="p-8 flex flex-col justify-center">
          <div className="label-eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-brand-primary" />
            Sign in
          </div>
          <h2 className="font-serif text-2xl text-brand-ink mt-3">Log in to your HER Aligned account</h2>
          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <label className="label-eyebrow block mb-2" htmlFor="weekly-email">Email</label>
              <input id="weekly-email" type="email" autoComplete="email" className="input-warm text-lg"
                placeholder="you@example.com" value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="weekly-login-email" autoFocus />
            </div>
            <div>
              <label className="label-eyebrow block mb-2" htmlFor="weekly-pw">Password</label>
              <input id="weekly-pw" type="password" autoComplete="current-password" className="input-warm text-lg"
                value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                data-testid="weekly-login-password" />
            </div>
            <button type="submit" disabled={!form.email || !form.password || busy}
              className="btn-primary inline-flex items-center gap-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="weekly-login-submit">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : <>Log in <ArrowRight size={16} /></>}
            </button>
          </form>
          <p className="mt-6 text-sm text-brand-ink-muted">
            <Link to="/forgot-password" className="text-brand-primary font-medium hover:underline" data-testid="weekly-forgot-link">Forgot your password?</Link>
          </p>
          <p className="mt-2 text-sm text-brand-ink-muted">
            Don&apos;t have an account yet?{" "}
            <Link to="/audit/signup" className="text-brand-primary font-medium hover:underline" data-testid="weekly-signup-link">Start with your Personalised Roadmap</Link>.
          </p>
        </div>
      </div>
    </section>
  );
}

function RoadmapGate() {
  return (
    <section className="surface-card mt-10 p-8" data-testid="weekly-roadmap-gate">
      <div className="flex items-start gap-4">
        <Compass className="text-brand-primary shrink-0 mt-1" size={22} strokeWidth={1.5} />
        <div>
          <h2 className="font-serif text-2xl text-brand-ink">Your Personalised Roadmap comes first.</h2>
          <p className="mt-3 text-brand-ink-muted text-base md:text-lg leading-relaxed max-w-2xl">
            Weekly Check-Ins build on your Personalised Roadmap. Complete your Roadmap first so your check-in setup and future insights can be genuinely personalised to you.
          </p>
          <Link to="/audit" className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="weekly-continue-roadmap-btn">
            Continue my Personalised Roadmap <ArrowRight size={16} strokeWidth={2} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function SetupForm({ preferredName, onCompleted }) {
  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [busy, setBusy] = useState(false);
  const nameToken = preferredName || "there";

  const canSubmit = q1.trim().length > 0 && q2.trim().length > 0 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await submitWeeklySetup({ main_goal_context: q1.trim(), identity_anchor: q2.trim() });
      toast.success("Your Weekly Check-In is set up.");
      await onCompleted();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't save your setup. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-10" data-testid="weekly-setup-form">
      <div className="label-eyebrow flex items-center gap-3">
        <span className="inline-block h-px w-10 bg-brand-primary" />
        First-time set up
      </div>
      <h2 className="font-serif font-semibold text-3xl text-brand-ink mt-3" data-testid="weekly-setup-heading">Let&apos;s set up your Weekly Check-In</h2>

      <form onSubmit={submit} className="mt-8 space-y-10">
        <div data-testid="weekly-setup-q1-block">
          <p className="text-base md:text-lg text-brand-ink leading-relaxed" data-testid="weekly-setup-q1-text">
            Hey {nameToken}, I already have your details from your Personalised Roadmap, but to set up your weekly check in properly, I&apos;d LOVE to understand you and your main goals in your own words as well as anything else that you think is important for me to understand about you?
          </p>
          <textarea
            className="input-warm mt-5 text-base leading-relaxed min-h-[180px]"
            placeholder="Share as much or as little as you'd like…"
            value={q1}
            onChange={(e) => setQ1(e.target.value)}
            data-testid="weekly-setup-q1-input"
            autoFocus
          />
        </div>

        <div data-testid="weekly-setup-q2-block">
          <p className="text-base md:text-lg text-brand-ink leading-relaxed whitespace-pre-line" data-testid="weekly-setup-q2-text">
            {`And lastly, it's easy to get caught up in the numbers and ticking boxes and as a result, lose yourself in the process.

So to prevent that I want to ask you:

`}<strong className="font-bold" data-testid="weekly-setup-identity-question">WHO is the woman you want to BECOME in this process?</strong>{`
What habits, behaviours, beliefs and values does she have?
And what signs and evidence will confirm to you that you've become her?

Take your time to reflect before you answer this :)`}
          </p>
          <textarea
            className="input-warm mt-5 text-base leading-relaxed min-h-[220px]"
            placeholder="Write your identity anchor here..."
            value={q2}
            onChange={(e) => setQ2(e.target.value)}
            data-testid="weekly-setup-q2-input"
          />
        </div>

        <div className="pt-2">
          <button type="submit" disabled={!canSubmit}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="weekly-setup-submit-btn">
            {busy ? <><Loader2 size={18} className="animate-spin" /> Saving your setup…</> : <>Finish setup <ArrowRight size={18} strokeWidth={2} /></>}
          </button>
        </div>
      </form>
    </section>
  );
}

export default function WeeklyCheckin() {
  const { client, loading: authLoading, refresh: refreshAuth, logout } = useClientAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ loading: false, data: null, error: null });
  const [inForm, setInForm] = useState(false);
  const [view, setView] = useState("current"); // current | history | snapshot
  const lockTimer = useRef(null); // after rating: show the final message, then auto-transition to LockedState
  useEffect(() => () => clearTimeout(lockTimer.current), []);

  const reload = async () => {
    setStatus({ loading: true, data: null, error: null });
    setInForm(false);
    try {
      const data = await getWeeklyStatus();
      setStatus({ loading: false, data, error: null });
    } catch (err) {
      const s = err?.response?.status;
      if (s === 401 || s === 403) setStatus({ loading: false, data: null, error: { kind: "auth" } });
      else setStatus({ loading: false, data: null, error: { kind: "server", message: err?.message || "Couldn't load your Weekly Check-In." } });
    }
  };

  useEffect(() => {
    if (!authLoading && client) reload();
    else if (!authLoading && !client) setStatus({ loading: false, data: null, error: null });
  }, [authLoading, client && client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSignedIn = async () => { await refreshAuth(); };
  const onSetupCompleted = async () => { await reload(); };
  const onCheckinCompleted = async (result, refreshOnly = false) => {
    if (!refreshOnly) toast.success("Your Weekly Check-In is complete.");
    await reload();
  };

  const body = useMemo(() => {
    if (authLoading) return <Loader2 className="text-brand-primary animate-spin mt-8" size={28} strokeWidth={1.5} />;
    if (!client) return <SignInPanel onSignedIn={onSignedIn} />;
    if (status.loading) return <Loader2 className="text-brand-primary animate-spin mt-8" size={28} strokeWidth={1.5} />;
    if (status.error?.kind === "auth") return <SignInPanel onSignedIn={onSignedIn} />;
    if (status.error) return (
      <section className="surface-card mt-10 p-8 text-center" data-testid="weekly-server-error">
        <p className="font-serif text-xl text-brand-ink">Couldn&apos;t load your Weekly Check-In.</p>
        <p className="mt-2 text-sm text-brand-ink-muted">{status.error.message}</p>
        <button className="btn-primary mt-6" onClick={reload} data-testid="weekly-retry-btn">Try again</button>
      </section>
    );
    const d = status.data;
    if (!d) return null;
    if (!d.roadmap_completed) return <RoadmapGate />;
    if (!d.setup_completed) return (
      <>
        <AccountabilityMessage preferredName={d.preferred_name} />
        <SetupForm preferredName={d.preferred_name} onCompleted={onSetupCompleted} />
      </>
    );
    const w = d.window;
    if (!w) return null;
    const nav = (
      <div className="mt-10 flex flex-wrap gap-2" role="tablist" data-testid="weekly-nav">
        {[["current", "This Week's Check-In"], ["history", "Weekly Insights History"], ["snapshot", "Results Snapshot"]].map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)}
            className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] font-bold border transition-colors ${view === k ? (k === "snapshot" ? "bg-[#C8965A] text-white border-[#C8965A] shadow-sm" : "bg-brand-ink text-white border-brand-ink") : "border-brand-border text-brand-ink-muted hover:text-brand-ink"}`}
            data-testid={`weekly-nav-${k}`}>{label}</button>
        ))}
      </div>
    );
    if (view === "history") return <>{nav}<InsightsHistory /></>;
    if (view === "snapshot") return <>{nav}<ResultsSnapshot /></>;
    if (w.state === "completed" && w.insights_status === "completed" && w.satisfaction_submitted) return (
      <>
        {nav}
        <LockedState identifier={w.identifier} />
      </>
    );
    if (w.state === "completed") return (
      <>
        {nav}
        <CompletedState preferredName={d.preferred_name} />
        {w.submission_id && <WeeklyInsights submissionId={w.submission_id} preferredName={d.preferred_name} onRated={() => {
          clearTimeout(lockTimer.current);
          lockTimer.current = setTimeout(() => {
            setStatus((prev) => prev.data ? { ...prev, data: { ...prev.data, window: { ...prev.data.window, insights_status: "completed", satisfaction_submitted: true } } } : prev);
          }, LOCK_AFTER_RATING_MS);
        }} />}
      </>
    );
    if (w.state === "closed") return <>{nav}<ClosedState status={d} /></>;
    return (
      <>
        {nav}
        <AccountabilityMessage preferredName={d.preferred_name} />
        {inForm
          ? <CheckinForm status={d} onCompleted={onCheckinCompleted} />
          : <OpenState preferredName={d.preferred_name} status={d} onBegin={() => setInForm(true)} />}
      </>
    );
  }, [authLoading, client, status, inForm, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const inSetup = Boolean(client && status.data && status.data.roadmap_completed && !status.data.setup_completed);

  return (
    <div className="min-h-screen bg-brand-base flex flex-col">
      <Toaster richColors position="top-center" />
      <BrandHeader small />
      <main className="flex-1 px-6 md:px-12 lg:px-16 py-10 md:py-14 max-w-4xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          <Intro showBodyCopy={!inSetup} />
          {body}
          {client && (
            <div className="mt-12 text-sm text-brand-ink-muted">
              Signed in as <span className="text-brand-ink">{client.email}</span> ·{" "}
              <button className="text-brand-primary hover:underline" onClick={async () => { await logout(); navigate("/weeklycheckin", { replace: true }); }} data-testid="weekly-logout-btn">
                Sign out
              </button>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
